import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { completeAuthRedirect, useSession } from '../lib/session';

/**
 * Full-screen branded loading, and — the part that matters — a failure path.
 * A callback that hangs is the worst thing that can happen at check-in, so if
 * no session has appeared after a few seconds this goes back to the login
 * screen with a sentence rather than sitting on a monogram forever.
 */
export default function AuthCallback() {
  const session = useSession();
  const location = useLocation() as { state?: { from?: string } };
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setGaveUp(true), 6000);
    return () => clearTimeout(id);
  }, []);

  // The PKCE exchange. Managed login sends the browser back here with `?code=`,
  // and this is where it becomes a session (D47). `error=` arrives instead when
  // the guest cancels at Google, which is a normal thing to do, not a fault.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) { if (params.get('error')) setGaveUp(true); return; }
    let live = true;
    completeAuthRedirect(code)
      .catch(() => { if (live) setGaveUp(true); })
      // Drop `?code=` so a reload does not retry a one-time code and fail.
      .finally(() => window.history.replaceState({}, '', '/auth/callback'));
    return () => { live = false; };
  }, []);

  if (session) {
    return <Navigate to={session.needsWelcome ? '/welcome' : (location.state?.from ?? '/now')} replace />;
  }
  if (gaveUp) {
    return <Navigate to="/" replace state={{ authError: true }} />;
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: 32 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 56 }}>M&amp;V</div>
        <p style={{ fontSize: 13, opacity: 0.6, marginTop: 10 }}>Signing you in…</p>
      </div>
    </div>
  );
}
